import React from 'react';
import { RefreshCw, CheckCircle2, XCircle, AlertTriangle, Database, GitBranch, Cpu, FileText, Layers, Clock } from 'lucide-react';
import { useHealth } from '../../../hooks/console/useConsoleAPI';

export default function HealthTab() {
    const { data, isLoading, isError, refetch, isFetching, dataUpdatedAt } = useHealth();

    const lastUpdated = dataUpdatedAt
        ? new Date(dataUpdatedAt).toLocaleTimeString('ko-KR')
        : '-';

    const dbOk     = !!data?.settings_loaded;
    const engineOk = !!data?.engine_version;   // env var 미설정이면 null
    const graphOk  = !!data?.graph_file;       // env var 미설정이면 null
    const allOk    = dbOk; // DB 연결이 핵심 — env var 미설정은 오류 아님

    const components = data ? [
        {
            icon: Cpu,
            label: '답변 엔진',
            desc: '질문을 받아 답변을 생성하는 핵심 엔진',
            color: 'blue',
            status: engineOk ? 'ok' : 'unset',   // null = 미설정
            detailLabel: '현재 버전',
            detail: data.engine_version ?? '미설정',
            sub: null,
        },
        {
            icon: Database,
            label: '데이터베이스',
            desc: '법령·FAQ 데이터가 저장된 데이터베이스',
            color: 'purple',
            status: dbOk ? 'ok' : 'error',        // DB는 실제 오류
            detailLabel: 'FAQ 문서 수',
            detail: (data.faq_count ?? 0).toLocaleString() + '건',
            sub: data.collection_name ? `컬렉션: ${data.collection_name}` : null,
        },
        {
            icon: GitBranch,
            label: '관계 그래프',
            desc: '법령 조문 간 연관 관계를 파악하는 지식 그래프',
            color: 'amber',
            status: graphOk ? 'ok' : 'unset',     // null = 미설정
            detailLabel: '그래프 파일',
            detail: data.graph_file ?? '미설정',
        },
        {
            icon: Layers,
            label: 'FAQ 자동 갱신',
            desc: 'FAQ 데이터를 정기적으로 자동 업데이트하는 배치',
            color: 'emerald',
            status: data.faq_batch_enabled ? 'ok' : 'unset',
            detailLabel: '실행 상태',
            detail: data.faq_batch_enabled
                ? `활성 — 매일 ${data.faq_batch_hour}시 실행`
                : '비활성',
        },
    ] : [];

    const colorMap = {
        blue:    { icon: 'text-blue-500',    bg: 'bg-blue-50' },
        purple:  { icon: 'text-purple-500',  bg: 'bg-purple-50' },
        amber:   { icon: 'text-amber-500',   bg: 'bg-amber-50' },
        emerald: { icon: 'text-emerald-500', bg: 'bg-emerald-50' },
    };

    return (
        <div className="space-y-4 w-full">
            {/* 헤더 */}
            <div className="flex items-center justify-between">
                <div>
                    <p className="text-lg font-bold text-sb-header">시스템 상태</p>
                    <p className="text-base text-gray-400">서비스를 구성하는 각 요소가 정상적으로 작동하는지 확인합니다.</p>
                </div>
                <div className="flex items-center gap-2">
                    <span className="text-sm text-gray-400">최근 확인: {lastUpdated}</span>
                    <button
                        onClick={() => refetch()}
                        disabled={isFetching}
                        className="flex items-center gap-1.5 px-3 py-2 bg-gray-100 hover:bg-gray-200 text-gray-700 text-base font-medium rounded-lg transition-colors"
                    >
                        <RefreshCw size={13} className={isFetching ? 'animate-spin' : ''} />
                        새로고침
                    </button>
                </div>
            </div>

            {/* 로딩 */}
            {isLoading && (
                <div className="text-center py-16">
                    <RefreshCw size={28} className="mx-auto mb-3 text-gray-300 animate-spin" />
                    <p className="text-base text-gray-400">시스템 상태 확인 중...</p>
                </div>
            )}

            {/* 서버 연결 오류 */}
            {isError && (
                <div className="rounded-2xl border border-red-200 bg-red-50 p-8 text-center">
                    <XCircle size={36} className="mx-auto mb-3 text-red-400" />
                    <p className="text-base font-bold text-red-600">서버에 연결할 수 없습니다</p>
                    <p className="text-base text-red-400 mt-1">엔진 서버가 실행 중인지 확인해 주세요.</p>
                </div>
            )}

            {!isLoading && !isError && data && (
                <>
                    {/* 전체 상태 배너 */}
                    <div className={`rounded-2xl p-5 flex items-center gap-4 ${allOk ? 'bg-emerald-50 border border-emerald-200' : 'bg-amber-50 border border-amber-200'}`}>
                        <div className={`w-12 h-12 rounded-full flex items-center justify-center shrink-0 ${allOk ? 'bg-emerald-100' : 'bg-amber-100'}`}>
                            {allOk
                                ? <CheckCircle2 size={24} className="text-emerald-600" />
                                : <AlertTriangle size={24} className="text-amber-600" />
                            }
                        </div>
                        <div className="flex-1">
                            <p className={`text-base font-bold ${allOk ? 'text-emerald-700' : 'text-amber-700'}`}>
                                {allOk ? '모든 시스템이 정상 작동 중입니다' : '일부 시스템에 문제가 있습니다'}
                            </p>
                            <p className={`text-base mt-0.5 ${allOk ? 'text-emerald-600' : 'text-amber-600'}`}>
                                {allOk
                                    ? '사용자 질의에 정상적으로 응답할 수 있는 상태입니다.'
                                    : '아래 항목을 확인하고 관리자에게 문의하세요.'
                                }
                            </p>
                        </div>
                        <div className="text-right shrink-0">
                            <p className="text-sm text-gray-400 flex items-center gap-1 justify-end mb-0.5">
                                <Clock size={10} /> 서버 시각
                            </p>
                            <p className="text-base font-mono font-medium text-gray-600">
                                {data.service_time
                                    ? new Date(data.service_time).toLocaleString('ko-KR')
                                    : '—'}
                            </p>
                        </div>
                    </div>

                    {/* 컴포넌트 카드 2×2 */}
                    <div className="grid grid-cols-2 gap-3">
                        {components.map((c) => {
                            const cm = colorMap[c.color];
                            const Icon = c.icon;
                            const borderCls = c.status === 'error'
                                ? 'border-red-200 bg-red-50/30'
                                : 'border-gray-100';
                            const badge = c.status === 'ok'
                                ? <span className="flex items-center gap-1 text-sm px-2 py-0.5 rounded-full bg-emerald-100 text-emerald-700 font-medium"><CheckCircle2 size={10} /> 정상</span>
                                : c.status === 'error'
                                ? <span className="flex items-center gap-1 text-sm px-2 py-0.5 rounded-full bg-red-100 text-red-600 font-medium"><XCircle size={10} /> 오류</span>
                                : <span className="flex items-center gap-1 text-sm px-2 py-0.5 rounded-full bg-gray-100 text-gray-500 font-medium">미설정</span>;
                            const detailColor = c.status === 'ok' ? cm.icon : c.status === 'error' ? 'text-red-500' : 'text-gray-400';
                            return (
                                <div key={c.label} className={`rounded-xl border bg-white p-4 shadow-sm ${borderCls}`}>
                                    <div className="flex items-start justify-between mb-3">
                                        <div className={`w-1 h-1 rounded-lg flex items-center justify-center ${cm.bg}`}>
                                            <Icon size={17} className={cm.icon} />
                                        </div>
                                        {badge}
                                    </div>
                                    <p className="text-base font-bold text-sb-header">{c.label}</p>
                                    <p className="text-sm text-gray-400 mt-0.5 leading-relaxed">{c.desc}</p>
                                    <div className="mt-3 pt-3 border-t border-gray-100">
                                        <p className="text-sm text-gray-400 mb-0.5">{c.detailLabel}</p>
                                        <p className={`text-base font-bold ${detailColor}`}>{c.detail}</p>
                                        {c.sub && (
                                            <p className="text-sm text-gray-400 font-mono mt-1 truncate">{c.sub}</p>
                                        )}
                                    </div>
                                </div>
                            );
                        })}
                    </div>

                    {/* 기술 세부 정보 (접기/펼치기) */}
                    <details className="rounded-xl border border-gray-100 bg-white shadow-sm">
                        <summary className="px-4 py-3 text-base font-medium text-gray-500 cursor-pointer select-none hover:text-gray-700 flex items-center gap-2">
                            <FileText size={13} /> 기술 세부 정보
                        </summary>
                        <div className="px-4 pb-4 pt-1 grid grid-cols-2 gap-x-6 gap-y-2.5">
                            {[
                                { label: '앱 버전',       value: data.app_version },
                                { label: '엔진 버전',     value: data.engine_version },
                                { label: '엔진 서버 URL', value: data.asip_base_url },
                                { label: '벡터DB 경로',   value: data.vector_db_path },
                                { label: '컬렉션명',      value: data.collection_name },
                                { label: '그래프 파일',   value: data.graph_file },
                                { label: 'FAQ 문서 수',   value: `${(data.faq_count ?? 0).toLocaleString()}건` },
                                { label: 'FAQ 배치',      value: data.faq_batch_enabled ? `활성 (매일 ${data.faq_batch_hour}시)` : '비활성' },
                            ].map((r) => (
                                <div key={r.label}>
                                    <p className="text-sm uppercase tracking-wider text-gray-400">{r.label}</p>
                                    <p className="text-base font-mono text-gray-700 mt-0.5 break-all">{r.value ?? '—'}</p>
                                </div>
                            ))}
                        </div>
                    </details>
                </>
            )}
        </div>
    );
}
